import React, { useCallback, useEffect, useState } from 'react';

import { useSelector } from 'react-redux';

import interactionResponse from 'await-interaction-response';
import clsx from 'clsx';
import _debounce from 'lodash/debounce';
import _get from 'lodash/get';

import { useDevice } from '@bonnet/core/device';

import { useFeatures } from '@atc/features';
import { getScaledImageSource } from '@atc/images';
import { waitUntil } from '@atc/wait-until';

import {
    ResponsiveImage,
    SideBarToModal,
} from 'reaxl';

import { breakpoints } from 'axl-config';

import vdpResultsDuck from '@/ducks/vdp/vdpResultsDuck.js';

import { mainImageSizes, thumbnailImageSizes } from './sizes';

export default function MediaGalleryImage({
    type = 'thumbnail',
    mediaType = 'md',
    isPlaceholder,
    isSlider = false,
    hasFyusion,
    src = '',
    onClick = () => { },
    lazyImage = false,
    classNames = '',
    ...rest
}) {
    const {
        image_optimization: [enableImageOptimization],
    } = useFeatures([
        'image_optimization',
    ]);

    const imageSize = {};
    const [showFyusionModal, setFyusionShowModal] = useState(false);
    const {
        title,
        vin,
    } = useSelector(vdpResultsDuck.selectors.getVdpActiveInventory);

    const device = useDevice();
    const isXs = _get(device, 'is.xs', false);

    // if type is fullscreen, don't set width/height for image
    if (type === 'video' || type === 'thumbnail') {
        imageSize.width = thumbnailImageSizes[mediaType]?.width;
        imageSize.height = thumbnailImageSizes[mediaType]?.height;
    } else if (type === 'main' || type === 'preview') {
        imageSize.width = mainImageSizes[mediaType]?.width;
        imageSize.height = mainImageSizes[mediaType]?.height;
    }

    const verifiedImgSrc = enableImageOptimization ? src.replace(/images.autotrader.com|assets.cai-media-management.com/, 'images2.autotrader.com') : src;
    const imgSrc = getScaledImageSource({
        src: verifiedImgSrc,
        ...imageSize,
        bgColor: '2d363e',
        isOptimized: enableImageOptimization,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const handleResize = useCallback(_debounce(() => {
        if (typeof window.CMFyusion !== 'undefined') {
            window.CMFyusion?.AppendTo('fyusionPlayer');
        }
    }, 250, { maxWait: 1000 }), []);

    useEffect(() => {
        const spin360 = document.getElementById('spin360');

        if (!spin360) return undefined;

        if (!!hasFyusion && typeof window !== 'undefined' && 'ResizeObserver' in window) {
            // Using ResizeObserver to handle resizing
            const resizeObserver = new ResizeObserver(handleResize);
            resizeObserver.observe(spin360);

            return () => {
                resizeObserver.disconnect();
            };
        }

        return undefined;

    }, [handleResize, hasFyusion]);

    const imageClassNames = clsx('media-gallery-image cursor-pointer', classNames);

    const handleShowFyusionModal = async () => {
        await interactionResponse();
        setFyusionShowModal(true);
        waitUntil(() => !!document?.getElementById('fyusionPlayer'), () => {
            if (typeof window.CMFyusion !== 'undefined') {
                window.CMFyusion?.addViewer(vin, 'fyusionPlayer');
            }
        }, 100, 5000);
    };

    const handleCloseModal = async () => {
        await interactionResponse();
        setFyusionShowModal(false);
    };

    const renderFyusionModal = () => (
        <SideBarToModal
            data-cmp="modal-fyusion-player"
            key="renderFyusionModal"
            collapsed={!!isXs}
            content={(
                <div
                    id="fyusionPlayer"
                />
            )}
            modalOptions={{
                bsSize: 'large',
            }}
            onHide={handleCloseModal}
            sideBarOptions={{
                fullWidth: true,
            }}
            show={showFyusionModal}
            title={title}
        />
    );
    if (type !== 'fullscreen') {
        rest.lgSize = type === 'main' || type === 'preview' ? mainImageSizes.lg : thumbnailImageSizes.lg;
        rest.mdSize = type === 'main' || type === 'preview' ? mainImageSizes.md : thumbnailImageSizes.md;
        rest.smSize = type === 'main' || type === 'preview' ? mainImageSizes.sm : thumbnailImageSizes.sm;
        rest.xsSize = type === 'main' || type === 'preview' ? mainImageSizes.xs : thumbnailImageSizes.xs;
        if (type === 'main') {
            rest.sizes = `(min-width: ${breakpoints.min.lg}px) ${mainImageSizes.lg.width}px, (min-width: ${breakpoints.min.md}px) ${mainImageSizes.md.width}px, (min-width: ${breakpoints.min.sm}px) ${mainImageSizes.sm.width}px, ${mainImageSizes.xs.width}px`;
        }
    }

    // Set image lazyload to false so that img tag always renders, but set loading to eager/lazy appropriately.
    // This is because intersection observer doesn't play nice with the scrollable panel inside the modal
    // and won't load things until the image is already in view.
    return (
        <div
            className={clsx(
                {
                    'img-rounded': isSlider && !isXs,
                    'media-slider-image-wrapper': isSlider,
                    'media-image-wrapper': !isSlider,
                    'rounded display-flex align-items-center overflow-hidden': !isSlider && ['main', 'thumbnail'].includes(type),
                    'bg-gray-darker': type !== 'preview' && !isSlider,
                },
            )}
            style={isSlider ? { '--bg-url': `url(${imgSrc})` } : undefined}
        >
            <ResponsiveImage
                src={imgSrc}
                onClick={hasFyusion ? handleShowFyusionModal : onClick}
                lazyload={false}
                loading={lazyImage ? 'lazy' : 'eager'}
                className={imageClassNames}
                {...rest}
            />
            {!!hasFyusion && renderFyusionModal()}
        </div>
    );
}
